import { useState } from "react";
import { CircleAlert, Trash2 } from "lucide-react";
import { btnAccent, btnDanger, btnSecondary } from "./admin-ui";
import { Dialog } from "./dialog";

export type ConfirmRequest = {
  title: string;
  message: string;
  // Optional highlighted line, e.g. the amount that will be charged.
  detail?: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
};

// Brand-styled confirmation, replacing the browser's native confirm(), on the
// shared Dialog shell. Controlled: pass `request` to open it, `onClose` clears
// it.
export function ConfirmDialog({
  request,
  onClose,
}: {
  request: ConfirmRequest | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={Boolean(request)}
      onClose={onClose}
      title={request?.title ?? ""}
      description={request?.message}
      icon={request?.destructive ? Trash2 : CircleAlert}
      tone={request?.destructive ? "ink" : "lemon"}
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={`${btnSecondary} px-4 py-2.5 text-sm`}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => {
              request?.onConfirm();
              onClose();
            }}
            className={`${request?.destructive ? btnDanger : btnAccent} px-4 py-2.5 text-sm`}
          >
            {request?.confirmLabel}
          </button>
        </>
      }
    >
      {request?.detail && (
        <p className="border-ink bg-lemon border-chip rounded-control p-3 font-mono text-sm font-bold">
          {request.detail}
        </p>
      )}
    </Dialog>
  );
}

type Ask = Omit<ConfirmRequest, "onConfirm">;

/**
 * `confirm()` with the brand dialog: `const [askConfirm, confirmDialog] =
 * useConfirm()`, render `confirmDialog` once, then `if (!(await askConfirm({…})))
 * return;` wherever the native confirm used to be.
 */
export function useConfirm() {
  const [pending, setPending] = useState<
    (Ask & { resolve: (ok: boolean) => void }) | null
  >(null);
  const ask = (request: Ask) =>
    new Promise<boolean>((resolve) => setPending({ ...request, resolve }));
  const dialog = (
    <ConfirmDialog
      request={
        pending && { ...pending, onConfirm: () => pending.resolve(true) }
      }
      onClose={() => {
        // Resolving twice is a no-op, so confirming then closing stays true.
        pending?.resolve(false);
        setPending(null);
      }}
    />
  );
  return [ask, dialog] as const;
}
